import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Navbar, Spinner } from 'react-bootstrap';
import { useI18n } from '../i18n';
import { harnessAuthAPI } from '../utils/httpClient';

/**
 * Agent tab — Django parent side of the M19.5 Django->harness auth bridge.
 *
 * INV-M19.5-AGENT-TAB-SENDER-001:
 *  - exchange the Django access token for a short-lived harness token via
 *    `harnessAuthAPI.exchange` (never the harness passwordless /auth/login),
 *  - iframe the harness SPA (env URL) only AFTER a successful exchange,
 *  - deliver the harness token to the iframe via postMessage with a TARGETED
 *    origin (never '*', never in the iframe URL/query) once the iframe loads,
 *  - re-exchange (renew) shortly before expiry using the still-valid Django
 *    token and push the new token,
 *  - on exchange failure show an error and do NOT mount a tokenless iframe,
 *  - clear token state on logout.
 */

// Message schema agreed with the harness SPA receiver (INV-M19.5-AGENT-TAB-RECEIVER-001).
const AGENT_AUTH_MESSAGE_TYPE = 'translation-harness/agent-auth';

// Renew this many ms before the harness token expires.
const RENEW_BUFFER_MS = 60 * 1000;

// Read at call time (not module load) so build-time env is always current and
// tests can configure it per-case.
const resolveHarnessAppUrl = () => {
  try {
    if (typeof process !== 'undefined' && process.env && process.env.REACT_APP_HARNESS_APP_URL) {
      return String(process.env.REACT_APP_HARNESS_APP_URL).trim();
    }
  } catch (_) { /* ignore */ }
  return '';
};

// Derive the exact postMessage target origin from the configured harness app URL.
// This is NEVER '*' and is NEVER taken from message contents.
const resolveHarnessOrigin = () => {
  const appUrl = resolveHarnessAppUrl();
  if (!appUrl) return '';
  try {
    return new URL(appUrl).origin;
  } catch (_) {
    return '';
  }
};

const getDjangoAccessToken = () =>
  localStorage.getItem('authToken') || localStorage.getItem('token') || '';

const Agent = () => {
  const { t } = useI18n();
  const navigate = useNavigate();

  const iframeRef = useRef(null);
  const renewTimerRef = useRef(null);
  const tokenRef = useRef(null); // { accessToken, expiresAt(ms) }

  const [status, setStatus] = useState('loading'); // 'loading' | 'ready' | 'error'

  const harnessAppUrl = resolveHarnessAppUrl();
  const harnessOrigin = resolveHarnessOrigin();

  const postTokenToIframe = useCallback((accessToken, expiresAt, principal) => {
    const frame = iframeRef.current;
    if (!frame || !frame.contentWindow || !harnessOrigin || !accessToken) return;
    // SAY WHO THIS TOKEN IS FOR. The harness SPA cannot work it out: a renewal for the SAME
    // person whose identity request blips and a token pushed for a DIFFERENT person whose
    // identity request blips look identical to it, and they need opposite handling of the
    // operator's unsent draft and paid proposal. This page knows — it just exchanged the
    // session. Omitted when the exchange did not report it, which the receiver accepts
    // unchanged.
    const message = principal
      ? { type: AGENT_AUTH_MESSAGE_TYPE, accessToken, expiresAt, principal }
      : { type: AGENT_AUTH_MESSAGE_TYPE, accessToken, expiresAt };
    frame.contentWindow.postMessage(
      message,
      harnessOrigin, // TARGETED origin — never '*'
    );
  }, [harnessOrigin]);

  const clearRenewTimer = useCallback(() => {
    if (renewTimerRef.current) {
      clearTimeout(renewTimerRef.current);
      renewTimerRef.current = null;
    }
  }, []);

  // doExchange performs the (re-)exchange. `renewFnRef` breaks the callback cycle
  // between doExchange and the renewal scheduler.
  const doExchangeRef = useRef(null);

  const scheduleRenew = useCallback((expiresAtMs) => {
    clearRenewTimer();
    if (!expiresAtMs || Number.isNaN(expiresAtMs)) return;
    const delay = Math.max(0, expiresAtMs - Date.now() - RENEW_BUFFER_MS);
    renewTimerRef.current = setTimeout(() => {
      if (doExchangeRef.current) doExchangeRef.current();
    }, delay);
  }, [clearRenewTimer]);

  const doExchange = useCallback(async () => {
    const djangoToken = getDjangoAccessToken();
    if (!djangoToken) {
      setStatus('error');
      return;
    }
    try {
      const data = await harnessAuthAPI.exchange(djangoToken);
      const accessToken = data && data.access_token;
      if (!accessToken) {
        setStatus('error');
        return;
      }
      const expiresAtMs = data.expires_at
        ? Date.parse(data.expires_at)
        : Date.now() + (Number(data.expires_in) || 0) * 1000;
      const principal = (data && data.user_id) || null;
      tokenRef.current = { accessToken, expiresAt: expiresAtMs, principal };
      setStatus('ready');
      // If the iframe is already mounted (renewal case), push immediately.
      postTokenToIframe(accessToken, expiresAtMs, principal);
      scheduleRenew(expiresAtMs);
    } catch (_) {
      // Exchange failed: surface an error; do NOT mount a tokenless iframe as authed.
      setStatus('error');
    }
  }, [postTokenToIframe, scheduleRenew]);

  useEffect(() => {
    doExchangeRef.current = doExchange;
  }, [doExchange]);

  useEffect(() => {
    doExchange();
    return () => { clearRenewTimer(); };
    // Run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Deliver the current token once the SPA iframe has loaded and can receive it.
  const handleIframeLoad = useCallback(() => {
    if (tokenRef.current) {
      postTokenToIframe(
        tokenRef.current.accessToken,
        tokenRef.current.expiresAt,
        tokenRef.current.principal,
      );
    }
  }, [postTokenToIframe]);

  const handleLogout = useCallback(() => {
    clearRenewTimer();
    tokenRef.current = null;
    localStorage.removeItem('token');
    localStorage.removeItem('authToken');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('userInfo');
    localStorage.removeItem('user');
    navigate('/login');
  }, [clearRenewTimer, navigate]);

  const header = (
    <Navbar bg="primary" variant="dark" className="px-3">
      <Navbar.Brand style={{ fontWeight: 500 }}>🤖 {t('choice:agent_title')}</Navbar.Brand>
      <div className="ms-auto d-flex gap-2">
        <Button variant="outline-light" size="sm" onClick={() => navigate('/choice')}>
          {t('choice:agent_back')}
        </Button>
        <Button variant="outline-light" size="sm" onClick={handleLogout}>
          {t('common:actions.logout')}
        </Button>
      </div>
    </Navbar>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      {header}
      <div style={{ flex: 1, position: 'relative' }}>
        {status === 'error' && (
          <div
            role="alert"
            data-testid="agent-error"
            style={{ padding: '2rem', textAlign: 'center' }}
          >
            <p>{t('choice:agent_error')}</p>
            <Button variant="primary" size="sm" onClick={() => { setStatus('loading'); doExchange(); }}>
              {t('choice:agent_retry')}
            </Button>
          </div>
        )}

        {status === 'loading' && (
          <div data-testid="agent-loading" style={{ padding: '2rem', textAlign: 'center' }}>
            <Spinner animation="border" role="status" size="sm" className="me-2" />
            {t('choice:agent_loading')}
          </div>
        )}

        {status === 'ready' && (
          <iframe
            ref={iframeRef}
            data-testid="agent-iframe"
            title={t('choice:agent_title')}
            src={harnessAppUrl}
            onLoad={handleIframeLoad}
            style={{ border: 'none', width: '100%', height: '100%' }}
          />
        )}
      </div>
    </div>
  );
};

export default Agent;
